import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { Translate } from 'react-redux-i18n'
import TooltipExpression from '../../../common/tooltipExpression'
import {
  getNoDataComponent,
  getStyleHeightContainer,
  handleIconDownload,
} from '../../../helper'
import classes from '../../../index.module.scss'
import { selectDataViewByTrend, selectIndicatorById } from '../store/slice'
import ChartLeft from './ChartLeft'
import ChartRight from './ChartRight'

const Trend = ({ isDynamicReport, id, check = [], isDownloadComponent }) => {
  const dataByTrend = useSelector(selectDataViewByTrend)
  const indicatorById = useSelector(selectIndicatorById)

  const propsChartROE = {
    xAxisKey: 'year',
    line: {
      dataKey: 'RT0060',
      label: indicatorById['RT0060']?.ratioName,
      color: '#006AA6',
    },
    area: {
      dataKey: 'RTI0060',
      label: indicatorById['RTI0060']?.ratioName,
      color: '#BCDBED',
      dotColor: '#97C3D8',
    },
    title: 'ROE',
    labelTopColor: '#006AA6',
    labelBottomColor: '#BCDBED',
  }

  const propsChartROA = {
    xAxisKey: 'year',
    line: {
      dataKey: 'RT0059',
      label: indicatorById['RT0059']?.ratioName,
      color: '#F49966',
    },
    area: {
      dataKey: 'RTI0059',
      label: indicatorById['RTI0059']?.ratioName,
      color: '#F8D2AD',
      dotColor: '#E9BB8E',
    },
    title: 'ROA',
    labelTopColor: '#F49966',
    labelBottomColor: '#F8D2AD',
  }

  const propsChartCost = {
    xAxisKey: 'year',
    dataKey: 'FA082',
    label: indicatorById['FA082']?.ratioName,
    color: '#006AA6',
    title: 'financialAnalysis.COST_TO_INCOME_RATIO',
    tooltipContent: (
      <div>
        <TooltipExpression
          expressions={[
            {
              text: <Translate value="financialAnalysis.INCOME_RATIO_1" />,
              operator: '=',
            },
            {
              text: 'A',
              isDivide: true,
              textUnder: 'B',
            },
          ]}
        />
        <div className="fw600 mt2 mb2">
          <Translate value="financialAnalysis.IN_WHICH" />
        </div>
        <TooltipExpression
          style={{ alignItems: 'unset' }}
          expressions={[
            {
              text: 'A',
              operator: '=',
              styleContainer: { alignItems: 'unset' },
            },
            {
              text: (
                <>
                  <Translate value="financialAnalysis.INCOME_RATIO_2" />
                  <br />
                  +&nbsp;
                  <Translate value="financialAnalysis.INCOME_RATIO_3" />
                  <br />
                  +&nbsp;
                  <Translate value="financialAnalysis.INCOME_RATIO_4" />
                  <br />
                  -&nbsp;
                  <Translate value="financialAnalysis.INCOME_RATIO_5" />
                  <br />
                  -&nbsp;
                  <Translate value="financialAnalysis.INCOME_RATIO_6" />
                  <br />
                  -&nbsp;
                  <Translate value="financialAnalysis.INCOME_RATIO_7" />
                  <br />
                  -&nbsp;
                  <Translate value="financialAnalysis.INCOME_RATIO_8" />
                </>
              ),
              styleText: { textAlign: 'unset' },
              styleContainer: { alignItems: 'unset' },
            },
          ]}
        />
        <div className="mt2">
          <TooltipExpression
            style={{ alignItems: 'unset' }}
            expressions={[
              {
                text: 'B',
                operator: '=',
                styleContainer: { alignItems: 'unset' },
              },
              {
                text: (
                  <>
                    <Translate value="financialAnalysis.INCOME_RATIO_9" />
                    <br />
                    +&nbsp;
                    <Translate value="financialAnalysis.INCOME_RATIO_10" />
                    <br />
                    -&nbsp;
                    <Translate value="financialAnalysis.INCOME_RATIO_11" />
                    <br />
                    -&nbsp;
                    <Translate value="financialAnalysis.INCOME_RATIO_12" />
                    <br />
                    -&nbsp;
                    <Translate value="financialAnalysis.INCOME_RATIO_13" />
                  </>
                ),
                styleText: { textAlign: 'unset' },
                styleContainer: { alignItems: 'unset' },
              },
            ]}
          />
        </div>
      </div>
    ),
  }

  const propsChartLoans = {
    xAxisKey: 'year',
    dataKey: 'FA083',
    label: indicatorById['FA083']?.ratioName,
    color: '#F49966',
    title: 'financialAnalysis.INCOME_LOANS',
  }

  const isNoData = !dataByTrend.length

  useEffect(() => {
    handleIconDownload(id, isNoData)
  }, [isNoData])

  if (isNoData) {
    return getNoDataComponent(isDynamicReport)
  }

  const chart = []
  if (!isDynamicReport || !isDownloadComponent) {
    chart.push(...[1, 2, 3, 4])
  } else {
    if (check.includes('FA082')) {
      chart.push(1)
    }
    if (check.includes('RT0060')) {
      chart.push(2)
    }
    if (check.includes('FA083')) {
      chart.push(3)
    }
    if (check.includes('RT0059')) {
      chart.push(4)
    }
  }

  const isH100 = chart.length >= 3

  return (
    <div
      style={{
        height: isDownloadComponent
          ? '100%'
          : getStyleHeightContainer(
              isDynamicReport,
              isNoData,
              isH100 ? 332 : 332 / 2,
            ),
      }}
      className={classes.earningQualitySecuContainer}
    >
      {chart.includes(1) && (
        <div className={isH100 ? classes.chartH50 : classes.chartH100}>
          <ChartLeft
            data={dataByTrend}
            propsChart={propsChartCost}
            isDynamicReport={isDynamicReport || isDownloadComponent}
          />
        </div>
      )}
      {chart.includes(2) && (
        <div className={isH100 ? classes.chartH50 : classes.chartH100}>
          <ChartRight
            data={dataByTrend}
            propsChart={propsChartROE}
            isDynamicReport={isDynamicReport || isDownloadComponent}
          />
        </div>
      )}
      {chart.includes(3) && (
        <div className={isH100 ? classes.chartH50 : classes.chartH100}>
          <ChartLeft
            data={dataByTrend}
            propsChart={propsChartLoans}
            isDynamicReport={isDynamicReport || isDownloadComponent}
          />
        </div>
      )}
      {chart.includes(4) && (
        <div className={isH100 ? classes.chartH50 : classes.chartH100}>
          <ChartRight
            data={dataByTrend}
            propsChart={propsChartROA}
            isDynamicReport={isDynamicReport || isDownloadComponent}
          />
        </div>
      )}
    </div>
  )
}

export default Trend
